import { subject } from '@casl/ability';
import { Can } from '@casl/react';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { Link, useLocation } from 'react-router-dom';

import { Icon, IconName } from '../../../common/Icon';
import { useAbility } from '../../../contexts/AbilityContext/AbilityProvider';
import { TabIds, tabIdToActionMap, tabIdToResourceMap } from '../constants';
import { NavigationItem } from '../types';

const COMMON_ITEM_STYLES = 'flex  items-center p-3 gap-3 self-stretch';
const ACTIVE_STYLES =
  'text-md border-r-[3px] border-r-border-highlight border-[#CE3F4F] truncate text-ellipsis';
const INACTIVE_STYLES = 'text-gray-600 truncate text-ellipsis';
const ICON_STYLES = 'w-6 h-6 flex-shrink-0';
const LABEL_STYLES =
  'text-md  leading-6 text-text-label truncate text-ellipsis';

// Common ItemContent component to reduce duplication
function ItemContent({
  item,
  isActive,
}: {
  item: NavigationItem;
  isActive: boolean;
}): JSX.Element {
  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          item.onClick?.();
        }
      }}
      onClick={item.onClick}
      className="cursor-pointer flex items-center gap-2"
    >
      <div className="relative">
        {typeof item.icon === 'string' ? (
          <Icon name={item.icon as IconName} className={ICON_STYLES} />
        ) : (
          item.icon && <item.icon className={ICON_STYLES} />
        )}
        {item.notify && (
          <span className="absolute top-[-5px] right-[-5px] block h-2 w-2 bg-red-500 rounded-full" />
        )}
      </div>
      <span className={`${LABEL_STYLES} ${isActive ? ' font-[600]' : ''}`}>
        {item.label}
      </span>
    </div>
  );
}

export function SidebarItem({
  item,
  isActive,
  className,
  parentId,
}: {
  item: NavigationItem;
  isActive: boolean;
  parentId?: string;
  className?: string;
}): JSX.Element {
  const ability = useAbility();
  const activeClassName = isActive ? ACTIVE_STYLES : INACTIVE_STYLES;
  const location = useLocation();
  if (item.subItems) {
    return (
      <Disclosure defaultOpen>
        <DisclosureButton
          className={`${COMMON_ITEM_STYLES} ${
            item.subItems.length === 0 ? activeClassName : ''
          }`}
        >
          <Can
            I={tabIdToActionMap[item.id as TabIds]}
            a={tabIdToResourceMap[item.id as TabIds]}
            ability={ability}
          >
            <ItemContent item={item} isActive={isActive} />
          </Can>
        </DisclosureButton>
        <DisclosurePanel className="w-full">
          {item.subItems?.map((subItem) => {
            return (
              <div key={subItem.id} className="w-full">
                <SidebarItem
                  parentId={item.id}
                  item={subItem}
                  isActive={location.pathname.includes(subItem.path)}
                />
              </div>
            );
          })}
        </DisclosurePanel>
      </Disclosure>
    );
  }
  return (
    <Can
      key={item.id}
      I={tabIdToActionMap[(parentId || item.id) as TabIds]}
      a={
        Object.values(TabIds).includes(item.id as TabIds)
          ? tabIdToResourceMap[item.id as TabIds]
          : subject(tabIdToResourceMap[(parentId || item.id) as TabIds], {
              teamId: item.id,
            })
      }
      ability={ability}
    >
      <Link
        to={item.path}
        className={`${COMMON_ITEM_STYLES} ${activeClassName} ${
          className ?? ''
        }`}
      >
        <ItemContent item={item} isActive={isActive} />
      </Link>
    </Can>
  );
}
